
import * as Cookie from "tiny-cookie";
import { defineComponent, ref, computed, PropType } from "vue";
import { usePlugin } from "@/plugins";

const STORAGE_TYPES = {
  local: "localStorage",
  cookies: "cookies",
};

export default defineComponent({
  name: "VueCookieLaw",
  props: {
    buttonText: {
      type: String,
      default: "Accept",
    },
    buttonDecline: {
      type: Boolean,
      default: false,
    },
    buttonDeclineText: {
      type: String,
      default: "Decline",
    },
    buttonLink: {
      type: [String, Object],
      required: false,
    },
    buttonLinkText: {
      type: String,
      default: "More info",
    },
    buttonLinkNewTab: {
      type: Boolean,
      default: false,
    },
    message: {
      type: String,
      default:
        "This website uses cookies to enhance your experience and providing the best service from us.",
    },
    theme: {
      type: String,
      default: "base",
    },
    /**
     * Cookie Container position
     * bottom, top
     * @type {Object}
     */
    position: {
      type: String,
      default: "bottom",
    },
    /**
     * Transition name has following possibilities
     * slideFromBottom
     * slideFromTop
     * fade
     * @type {Object}
     */
    transitionName: {
      type: String,
      default: "slideFromBottom",
    },
    buttonClass: {
      type: String,
      default: "Cookie__button",
    },
    buttonDeclineClass: {
      type: String,
      default: "Cookie__button--decline",
    },
    storageName: {
      type: String,
      default: "cookie:accepted",
    },
    storageType: {
      type: String,
      default: STORAGE_TYPES.local,
    },
    cookieOptions: {
      type: Object as PropType<Cookie.CookieOptions>,
      //   default: () => {},
      required: false,
    },
  },

  setup(props) {
    const { popupCookie, popupPersonalPolicy } = usePlugin();
    const supportsLocalStorage = ref(true);
    const isOpen = ref(false);

    const containerPosition = computed(() => `Cookie--${props.position}`);
    const cookieTheme = computed(() => `Cookie--${props.theme}`);
    const externalButtonLink = computed(() => {
      return typeof props.buttonLink === "string" && props.buttonLink.length;
    });
    const internalButtonLink = computed(() => {
      return (
        typeof props.buttonLink === "object" &&
        props.buttonLink != null &&
        Object.keys(props.buttonLink).length
      );
    });
    const target = computed(() =>
      props.buttonLinkNewTab ? "_blank" : "_self"
    );
    const canUseLocalStorage = computed(() => {
      return (
        props.storageType === STORAGE_TYPES.local && supportsLocalStorage.value
      );
    });

    const setVisited = () => {
      if (canUseLocalStorage.value) {
        localStorage.setItem(props.storageName, "true");
      } else {
        Cookie.set(props.storageName, "true", {
          ...props.cookieOptions,
          expires: "1Y",
        });
      }
    };

    const setAccepted = () => {
      if (canUseLocalStorage.value) {
        localStorage.setItem(props.storageName, "true");
      } else {
        Cookie.set(props.storageName, "true", {
          ...props.cookieOptions,
          expires: "1Y",
        });
      }
    };
    const setDeclined = () => {
      if (canUseLocalStorage.value) {
        localStorage.setItem(props.storageName, "false");
      } else {
        Cookie.set(props.storageName, "false", {
          ...props.cookieOptions,
          expires: "1Y",
        });
      }
    };

    const isAccepted = () => {
      let accepted: string | null = "false";
      if (canUseLocalStorage.value) {
        accepted = localStorage.getItem(props.storageName);
      } else {
        accepted = Cookie.get(props.storageName);
      }

      if (typeof accepted === "string") {
        accepted = JSON.parse(accepted);
      }

      return accepted;
    };

    const getVisited = () => {
      let visited: string | null = "false";
      if (canUseLocalStorage.value) {
        visited = localStorage.getItem(props.storageName);
      } else {
        visited = Cookie.get(props.storageName);
      }

      if (typeof visited === "string") {
        visited = JSON.parse(visited);
      }
      return !(visited === null || visited === undefined);
    };

    const onCreate = () => {
      if (props.storageType === STORAGE_TYPES.local) {
        // Check for availability of localStorage
        try {
          const test = "__vue-cookielaw-check-localStorage";
          if (typeof window !== "undefined") {
            window.localStorage.setItem(test, test);
            window.localStorage.removeItem(test);
          }
        } catch (e) {
          console.info(
            "Local storage is not supported, falling back to cookie use"
          );
          supportsLocalStorage.value = false;
        }
      }

      if (!getVisited()) {
        isOpen.value = true;
      }
    };

    onCreate();

    return {
      supportsLocalStorage,
      isOpen,

      containerPosition,
      cookieTheme,
      externalButtonLink,
      internalButtonLink,

      target,
      canUseLocalStorage,

      setVisited,
      setAccepted,
      setDeclined,
      isAccepted,

      popupCookie,
      popupPersonalPolicy,

      getVisited,
    };
  },
  mounted() {
    if (this.isAccepted()) {
      this.$emit("accept");
    }
  },
  methods: {
    accept() {
      this.setVisited();
      this.setAccepted();
      this.isOpen = false;
      this.$emit("accept");
    },
    close() {
      this.isOpen = false;
      this.$emit("close");
    },
    decline() {
      this.setVisited();
      this.setDeclined();
      this.isOpen = false;
      this.$emit("decline");
    },
    revoke() {
      if (this.canUseLocalStorage) {
        localStorage.removeItem(this.storageName);
      } else {
        Cookie.remove(this.storageName);
      }
      this.isOpen = true;
      this.$emit("revoke");
    },
    open() {
      if (!this.getVisited()) {
        this.isOpen = true;
      }
    },
  },
});
